import { Button, SlideOutModal } from "../../../components";
import { Form, Button as BSButton, Card } from "react-bootstrap";
import * as yup from 'yup';
import { ErrorMessage, Formik } from 'formik';
import React, { useEffect, useRef, useState } from "react";
import { SchedulerFormProps } from "@progress/kendo-react-scheduler";
import { AutoComplete, AutoCompleteChangeEvent } from "@progress/kendo-react-dropdowns";
import { ZIndexContext } from "@progress/kendo-react-common";
import { AttendanceStatus, CustomerModel, EffectiveServiceDuration } from "../../../data/entities";
import { ServiceService, CustomerService, CalendarEventService } from "../../../services";
import { useSharedState, useTenantFeatures } from "../../../hooks";
import { formatDate, formatDateTimeLocal } from "../../../utils";

interface CalendarBookingModalProps extends SchedulerFormProps {
  dropdownResults?: any[];
  onDeleteAppointment?: (dataItem: any) => Promise<void> | void;
}

/** Notes this modal copied in from a customer's previous appointment (tenant feature "Auto-Copy Customer Notes"). */
interface AutoFilledNotes {
  notes: string;
  customerName: string;
  date: string;
}

export function CalendarBookingModal(props: CalendarBookingModalProps) {
  const submitRef = useRef<HTMLButtonElement>(null);
  const [servicesData, setServicesData] = useState<EffectiveServiceDuration[]>([]);
  const [isLoadingServices, setIsLoadingServices] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<string>("");
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [formValues, setFormValues] = useState<any>(null);
  const [customerSuggestions, setCustomerSuggestions] = useState<CustomerModel[]>([]);
  const [customerSearchValue, setCustomerSearchValue] = useState("");
  const customerSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const customerService = useRef(new CustomerService());

  // Auto-copy of the selected customer's most recent appointment notes (new appointments only)
  const { autoCopyCustomerNotes } = useTenantFeatures();
  const calendarEventService = useRef(new CalendarEventService());
  const isNewAppointment = !props.dataItem?.id;
  const [autoFilledNotes, setAutoFilledNotes] = useState<AutoFilledNotes | null>(null);
  const autoFilledNotesRef = useRef<AutoFilledNotes | null>(null); 
  const latestNotesRef = useRef<string>("");                         
  const notesRequestId = useRef(0);                                  

  const rememberAutoFilledNotes = (value: AutoFilledNotes | null) => {
    autoFilledNotesRef.current = value;
    setAutoFilledNotes(value);
  };

  const notesAreUntouched = () => {
    const current = latestNotesRef.current ?? "";
    return !current.trim() || (autoFilledNotesRef.current !== null && current === autoFilledNotesRef.current.notes);
  };

  const applyLatestCustomerNotes = async (customer: CustomerModel, setFieldValue: (field: string, value: any) => void) => {
    if (!autoCopyCustomerNotes || !isNewAppointment || !customer.id) return;

    const requestId = ++notesRequestId.current;
    const latest = await calendarEventService.current.getLatestCustomerNotesAsync(customer.id);
    if (requestId !== notesRequestId.current) return; 
    if (!notesAreUntouched()) return;

    const copied = latest?.notes?.trim() ?? "";
    if (copied) {
      rememberAutoFilledNotes({
        notes: copied,
        customerName: `${customer.firstName ?? ""} ${customer.lastName ?? ""}`.trim(),
        date: latest!.date,
      });
      setFieldValue("notes", copied);
    } else if (autoFilledNotesRef.current) {
      rememberAutoFilledNotes(null);
      setFieldValue("notes", "");
    }
  };

  const clearAutoFilledNotes = (setFieldValue: (field: string, value: any) => void) => {
    notesRequestId.current++; 
    if (autoFilledNotesRef.current && latestNotesRef.current === autoFilledNotesRef.current.notes) {
      setFieldValue("notes", "");
    }
    rememberAutoFilledNotes(null);
  };

  const initialServiceIdRef = useRef<number | undefined>(undefined);
  const initialStartRef = useRef<Date | undefined>(undefined);
  
  const showState = useSharedState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [backdrop, setBackdrop] = useState<"static" | true>("static");
  useEffect(() => {
    const timer = setTimeout(() => setBackdrop(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const validationSchema = yup.object().shape({
    customerFirstName: yup.string().required('First name is required'),
    customerLastName: yup.string().required('Last name is required'),
    email: yup.string().email('Invalid email').notRequired(),
    phoneNumber: yup.string().notRequired(),
    locationId: yup.number().required('Location is required'),
    providerId: yup.number().required('Provider is required'),
    serviceId: yup.number().required('Service is required'),
    start: yup.date().required('Start time is required'),
    end: yup.date().required('End time is required')
      .min(yup.ref('start'), 'End time must be after start time'),
    notes: yup.string().notRequired(),
  });

  const seen = new Set();
  const locationOptions = props.dropdownResults
    ?.filter((location) => {
      const id = location?.locationId?.toString();
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .map((location) => ({
      value: location?.locationId,
      label: location?.locationName,
    })) || [];

  const isLocationLocked = locationOptions.length === 1;

  const providerOptions = props.dropdownResults
    ?.filter((p) => p.locationId.toString() === selectedLocation?.toString())
    .map((provider) => ({
      value: provider?.providerId,
      label: provider?.providerName,
    })) || [];

  const isProviderLocked = providerOptions.length === 1;

  const inactiveProviderId = props.dataItem?.providerId;
  const showInactiveProviderOption =
    !!inactiveProviderId &&
    selectedProvider === inactiveProviderId.toString() &&
    selectedLocation === props.dataItem?.locationId?.toString() &&
    !providerOptions.some((p) => p.value?.toString() === inactiveProviderId.toString());

  const initialValues = {
    id: props.dataItem?.id || undefined,
    customerFirstName: props.dataItem?.customerFirstName || "",
    customerLastName: props.dataItem?.customerLastName || "",
    email: props.dataItem?.email || "",
    phoneNumber: props.dataItem?.phoneNumber || "",
    locationId: props.dataItem?.locationId || (isLocationLocked ? locationOptions[0]?.value : ""),
    providerId: props.dataItem?.providerId || "",
    serviceId: props.dataItem?.serviceId || "",
    start: props.dataItem?.start ? new Date(props.dataItem.start) : new Date(),
    end: props.dataItem?.end ? new Date(props.dataItem.end) : new Date(),
    notes: props.dataItem?.notes || "",
    attendanceStatus: props.dataItem?.attendanceStatus ?? AttendanceStatus.Pending,
  };

  useEffect(() => {
    initialServiceIdRef.current = props.dataItem?.serviceId;
    initialStartRef.current = props.dataItem?.start ? new Date(props.dataItem.start) : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); 

  useEffect(() => {
    const fetchServices = async () => {
      if (!selectedLocation || !selectedProvider) {
        setServicesData([]);
        return;
      }

      setIsLoadingServices(true);
      try {
        const serviceService = new ServiceService();
        const services = await serviceService.getEffectiveServiceDurationAsync(
          Number(selectedLocation),
          Number(selectedProvider)
        );
        setServicesData(services);
      } catch (error) {
        console.error("Error fetching services:", error);
        setServicesData([]);
      } finally {
        setIsLoadingServices(false);
      }
    };

    fetchServices();
  }, [selectedLocation, selectedProvider]);

  // Auto-calculate end time only when user changes service or start time
  useEffect(() => {
    if (!formValues || !formValues.serviceId || !formValues.start || !servicesData.length) {
      return;
    }

    // Check if service or start time has actually changed from initial values
    const serviceChanged = initialServiceIdRef.current !== Number(formValues.serviceId);
    const startChanged = initialStartRef.current 
      ? initialStartRef.current.getTime() !== new Date(formValues.start).getTime()
      : false; 

    if (!serviceChanged && !startChanged) {
      return;
    }

    const serviceData = servicesData.find(s => s.serviceId === Number(formValues.serviceId));
    if (serviceData && formValues.setFieldValue) {
      const startDate = new Date(formValues.start);
      const newEndDate = new Date(startDate.getTime() + serviceData.effectiveTotalDuration * 60000);

      const currentEndTime = new Date(formValues.end).getTime();
      if (Math.abs(currentEndTime - newEndDate.getTime()) > 1000) {
        formValues.setFieldValue("end", newEndDate);
      }
    }
      // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.serviceId, formValues?.start, servicesData]);

  const handleClose = () => {
    showState.setter(false);
    if (props.onCancel) {
      props.onCancel({} as any);
    }
  };

  const handleDeleteConfirmed = async () => {
    if (!props.onDeleteAppointment) return;
    setIsDeleting(true);
    try {
      await props.onDeleteAppointment(props.dataItem);
      handleClose();
    } finally {
      setIsDeleting(false);
    }
  };

  const serviceOptions = servicesData.map((service) => ({
    value: service.serviceId,
    label: service.serviceName,
  }))
  .sort((a,b)=> a.label.localeCompare(b.label));

  return (
    <SlideOutModal
      showState={showState}
      onHide={handleClose}
      backdrop={backdrop}
      title={props.dataItem?.id ? "Edit Appointment" : "New Appointment"}
      footer={
        showDeleteConfirm ? (
          <div className="container p-0">
            <p className="text-center small mb-2">
              Delete this appointment? This cannot be undone.
            </p>
            <div className="row g-0">
              <div className="col-12 pe-2">
                <Button
                  onClick={() => setShowDeleteConfirm(false)}
                  variant="cancel"
                  className="w-100"
                  disabled={isDeleting}
                >
                  Keep Appointment
                </Button>
              </div>
              <div className="col-12 ps-2">
                <Button
                  onClick={handleDeleteConfirmed}
                  variant="danger"
                  className="w-100"
                  disabled={isDeleting}
                  showSpinner={isDeleting}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="container p-0">
            <div className="row g-0">
              <div className="col-12 pe-2">
                <Button onClick={handleClose} variant="cancel" className="w-100">
                  Cancel
                </Button>
              </div>
              <div className="col-12 ps-2">
                <Button onClick={() => submitRef.current?.click()} variant="primary" className="w-100">
                  Save Appointment
                </Button>
              </div>
            </div>
          </div>
        )
      }
    >
      <Formik
        validationSchema={validationSchema}
        initialValues={initialValues}
        enableReinitialize
        onSubmit={async (values) => {
          // Kendo's onSubmit
          if (props.onSubmit) {
            props.onSubmit({
              value: values,
              syntheticEvent: {} as any,
            } as any);
          }
        }}
      >
        {({ handleSubmit, values, setFieldValue, handleChange, handleBlur, errors, touched }) => {
          latestNotesRef.current = values.notes ?? "";

          if (formValues?.serviceId !== values.serviceId || formValues?.start !== values.start) {
            setFormValues({ ...values, setFieldValue });
          }

          // Update selected location/provider for fetching services
          if (selectedLocation !== values.locationId?.toString()) {
            setSelectedLocation(values.locationId?.toString() || "");
          }
          if (selectedProvider !== values.providerId?.toString()) {
            setSelectedProvider(values.providerId?.toString() || "");
          }

          // Auto-select location if only one option 
          if (isLocationLocked && !values.locationId && locationOptions.length === 1) {
            setFieldValue('locationId', locationOptions[0].value);
          }

          // Auto-select provider if only one option
          if (isProviderLocked && providerOptions.length === 1 && values.locationId && !values.providerId) {
            setFieldValue('providerId', providerOptions[0].value);
          }

          return (
            <Form onSubmit={handleSubmit}>
              {/* Customer Information Card */}
              <Card className="mb-3 shadow-sm">
                <Card.Body className="p-3">
                  <h5 className="mb-3">
                    <i className="fa fa-user me-2"></i>
                    Customer Information
                  </h5>

                  <Form.Group className="mb-3">
                    <Form.Label>Search Existing Customer</Form.Label>
                    <ZIndexContext.Provider value={10000}>
                      <AutoComplete
                        data={customerSuggestions.map(c => `${c.firstName} ${c.lastName}`)}
                        value={customerSearchValue}
                        placeholder="Type a name, phone, or email..."
                        className="w-100"
                        suggest={false}
                        itemRender={(li, itemProps) => {
                          const customer = customerSuggestions[itemProps.index];
                          return React.cloneElement(li, li.props,
                            <div className="d-flex flex-column">
                              <div className="fw-semibold lh-sm">
                                {customer?.firstName} {customer?.lastName}
                              </div>
                              <div className="text-muted small lh-sm">
                                {[customer?.phone, customer?.email].filter(Boolean).join(' · ')}
                              </div>
                            </div>
                          )}}
                        onChange={(e: AutoCompleteChangeEvent) => {
                          const val = e.value as string;
                          setCustomerSearchValue(val);

                          const match = customerSuggestions.find(c =>
                            `${c.firstName} ${c.lastName}` === val
                          );
                          if (match) {
                            setFieldValue('customerFirstName', match.firstName || '');
                            setFieldValue('customerLastName', match.lastName || '');
                            setFieldValue('email', match.email || '');
                            setFieldValue('phoneNumber', match.phone || '');
                            setCustomerSuggestions([]);
                            applyLatestCustomerNotes(match, setFieldValue);
                            return;
                          }

                          if (customerSearchTimeout.current) clearTimeout(customerSearchTimeout.current);
                          if (val.length >= 1) {
                            customerSearchTimeout.current = setTimeout(async () => {
                              const results = await customerService.current.searchAsync(val);
                              setCustomerSuggestions(results);
                            }, 300);
                          } else {
                            setCustomerSuggestions([]);
                          }
                        }}
                      />
                    </ZIndexContext.Provider>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>First Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="customerFirstName"
                      value={values.customerFirstName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.customerFirstName && !!errors.customerFirstName}
                    />
                    <ErrorMessage name="customerFirstName" component="div" className="text-danger small mt-1" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Last Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="customerLastName"
                      value={values.customerLastName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.customerLastName && !!errors.customerLastName}
                    />
                    <ErrorMessage name="customerLastName" component="div" className="text-danger small mt-1" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Phone (Optional)</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phoneNumber"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.phoneNumber && !!errors.phoneNumber}
                    />
                    <ErrorMessage name="phoneNumber" component="div" className="text-danger small mt-1" />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Email (Optional)</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={values.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.email && !!errors.email}
                    />
                    <ErrorMessage name="email" component="div" className="text-danger small mt-1" />
                  </Form.Group>

                  {(values.customerFirstName || values.customerLastName || values.email || values.phoneNumber || customerSearchValue) && (
                    <div className="text-end">
                      <small
                        role="button"
                        className="text-muted"
                        onClick={() => {
                          setFieldValue('customerFirstName', '');
                          setFieldValue('customerLastName', '');
                          setFieldValue('email', '');
                          setFieldValue('phoneNumber', '');
                          setCustomerSearchValue('');
                          setCustomerSuggestions([]);
                          clearAutoFilledNotes(setFieldValue);
                        }}
                        style={{
                          cursor: 'pointer',
                          textDecoration: 'underline'
                        }}
                      >
                        Clear customer information
                      </small>
                    </div>
                  )}
                </Card.Body>
              </Card>

              {/* Booking Information Card */}
              <Card className="mb-3 shadow-sm">
                <Card.Body className="p-3">
                  <h5 className="mb-3">
                    <i className="fa fa-calendar me-2"></i>
                    Booking Information
                  </h5>

                  <Form.Group className="mb-3">
                    <Form.Label>Location *</Form.Label>
                    <Form.Select
                      name="locationId"
                      value={values.locationId}
                      onChange={(e) => {
                        handleChange(e);
                        setFieldValue('providerId', '');
                        setFieldValue('serviceId', '');
                      }}
                      onBlur={handleBlur}
                      disabled={isLocationLocked}
                      isInvalid={touched.locationId && !!errors.locationId}
                      style={{ backgroundColor: isLocationLocked ? undefined : 'white' }}
                    >
                      <option value="">Select location...</option>
                      {locationOptions.map((loc) => (
                        <option key={loc.value} value={loc.value}>
                          {loc.label}
                        </option>
                      ))}
                    </Form.Select>
                    <ErrorMessage name="locationId" component="div" className="text-danger small mt-1" />
                  </Form.Group>

                  {values.locationId && (
                    <Form.Group className="mb-3">
                      <Form.Label>Provider *</Form.Label>
                      <Form.Select
                        name="providerId"
                        value={values.providerId}
                        onChange={(e) => {
                          handleChange(e);
                          setFieldValue('serviceId', '');
                        }}
                        onBlur={handleBlur}
                        disabled={isProviderLocked}
                        isInvalid={touched.providerId && !!errors.providerId}
                        style={{ backgroundColor: isProviderLocked ? undefined : 'white' }}
                      >
                        <option value="">Select provider...</option>
                        {showInactiveProviderOption && (
                          <option value={inactiveProviderId} disabled>
                            {props.dataItem?.providerName ?? `Provider ${inactiveProviderId}`} (inactive)
                          </option>
                        )}
                        {providerOptions.map((prov) => (
                          <option key={prov.value} value={prov.value}>
                            {prov.label}
                          </option>
                        ))}
                      </Form.Select>
                      <ErrorMessage name="providerId" component="div" className="text-danger small mt-1" />
                    </Form.Group>
                  )}

                  {values.providerId && (
                    <Form.Group className="mb-3">
                      <Form.Label>Service *</Form.Label>
                      {isLoadingServices ? (
                        <div className="text-muted small">Loading services...</div>
                      ) : (
                        <>
                          <Form.Select
                            name="serviceId"
                            value={values.serviceId}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.serviceId && !!errors.serviceId}
                          >
                            <option value="">Select service...</option>
                            {serviceOptions.map((serv) => (
                              <option key={serv.value} value={serv.value}>
                                {serv.label}
                              </option>
                            ))}
                          </Form.Select>
                          <ErrorMessage name="serviceId" component="div" className="text-danger small mt-1" />
                        </>
                      )}
                    </Form.Group>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>Start Time *</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      name="start"
                      value={formatDateTimeLocal(values.start)}
                      onChange={(e) => setFieldValue("start", new Date(e.target.value))}
                      onBlur={handleBlur}
                      isInvalid={touched.start && !!errors.start}
                    />
                    <ErrorMessage name="start" component="div" className="text-danger small mt-1" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>End Time *</Form.Label>
                    <Form.Control
                      type="datetime-local"
                      name="end"
                      value={formatDateTimeLocal(values.end)}
                      onChange={(e) => setFieldValue("end", new Date(e.target.value))}
                      onBlur={handleBlur}
                      isInvalid={touched.end && !!errors.end}
                    />
                    <ErrorMessage name="end" component="div" className="text-danger small mt-1" />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Notes For This Appointment Only (Optional)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="notes"
                      value={values.notes}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.notes && !!errors.notes}
                    />
                    {autoFilledNotes && values.notes === autoFilledNotes.notes && (
                      <Form.Text muted>
                        <i className="fa fa-clipboard me-1" />
                        Copied from {autoFilledNotes.customerName || "this customer"}'s most recent appointment
                        {autoFilledNotes.date ? ` on ${formatDate(autoFilledNotes.date)}` : ""}. 
                      </Form.Text>
                    )}
                    <ErrorMessage name="notes" component="div" className="text-danger small mt-1" />
                  </Form.Group>
                </Card.Body>
              </Card>

              {props.dataItem?.id && (() => {
                const appointmentHasStarted = values.start
                  ? new Date(values.start).getTime() <= Date.now()
                  : false;
                return (
                  <Card className="mb-3 shadow-sm">
                    <Card.Body className="p-3">
                      <h5 className="mb-3">
                        <i className="fa fa-check me-2"></i>
                        Attendance
                      </h5>
                      <div className="btn-group w-100" role="group">
                        <button
                          type="button"
                          className={`btn btn-sm ${values.attendanceStatus === AttendanceStatus.Pending ? "btn-primary" : "btn-outline-secondary"}`}
                          onClick={() => setFieldValue("attendanceStatus", AttendanceStatus.Pending)}
                        >
                          Booked
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${values.attendanceStatus === AttendanceStatus.CheckedIn ? "btn-success" : "btn-outline-secondary"}`}
                          onClick={() => setFieldValue("attendanceStatus", AttendanceStatus.CheckedIn)}
                        >
                          Checked in
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${values.attendanceStatus === AttendanceStatus.NoShow ? "btn-danger" : "btn-outline-secondary"}`}
                          onClick={() => setFieldValue("attendanceStatus", AttendanceStatus.NoShow)}
                          disabled={values.attendanceStatus !== AttendanceStatus.NoShow && !appointmentHasStarted}
                          title={appointmentHasStarted ? undefined : "Available once the appointment start time has passed"}
                        >
                          No-show
                        </button>
                      </div>
                    </Card.Body>
                  </Card>
                );
              })()}

              {props.dataItem?.id && props.onDeleteAppointment && (
                <div className="text-center mb-3">
                  <button
                    type="button"
                    className="appointment-delete-btn"
                    onClick={() => setShowDeleteConfirm(true)}
                  >
                    <i className="fa fa-trash" />
                    Delete Appointment
                  </button>
                </div>
              )}

              <BSButton ref={submitRef} type="submit" className="d-none" />
            </Form>
          );
        }}
      </Formik>
    </SlideOutModal>
  );
}

export default CalendarBookingModal;